import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { analyticsApi } from '../utils/api';
import { IconMonitor, IconStar, IconBarChart, IconArrowRight } from '../components/Icons';

const SECONDARY = '#e8a838';
const NAV_CARD = '#162231';
const BORDER_COLOR = '#2a3f56';
const TEXT_LIGHT = '#f0f2f5';
const SURFACE = '#1e2d3d';
const SURFACE_ALT = '#162231';
const SURFACE_HOVER = '#243548';
const TABLE_HEADER_BG = '#e8a838';
const TABLE_HEADER_TEXT = '#000000';

// ── Simple SVG bar chart ──────────────────────────────────────
function BarChart({ data }) {
  if (!data || data.length === 0) {
    return <div style={{ color: '#8a9bb5', padding: '32px 0', textAlign: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>No daily view data yet.</div>;
  }
  const maxVal = Math.max(...data.map(d => d.unique_viewers), 1);
  const chartH = 140;
  const labelH = 18;
  const barW = Math.min(40, Math.floor(560 / data.length) - 4);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={Math.max(560, data.length * (barW + 4))} height={labelH + chartH + 40} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const h = Math.max(4, Math.round((d.unique_viewers / maxVal) * chartH));
          const x = i * (barW + 4) + 2;
          const y = labelH + chartH - h;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={barW} height={h} rx={3} fill={SECONDARY} opacity={0.85} />
              <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT} fontFamily="IBM Plex Sans, sans-serif">
                {d.unique_viewers}
              </text>
              <text x={x + barW / 2} y={labelH + chartH + 16} textAnchor="middle" fontSize={9} fill="#8a9bb5" fontFamily="IBM Plex Sans, sans-serif"
                transform={`rotate(-35, ${x + barW / 2}, ${labelH + chartH + 16})`}>
                {d.date.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────
function KpiCard({ label, value, sub }) {
  return (
    <div style={{
      background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 10,
      padding: '20px 24px', flex: '1 1 180px', minWidth: 160
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 8, fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: TEXT_LIGHT, fontFamily: 'Oswald, sans-serif', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginTop: 4, fontFamily: 'IBM Plex Sans, sans-serif' }}>{sub}</div>}
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────
function SectionHeading({ icon, children }) {
  return (
    <h3 style={{ fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT, fontSize: '1.1rem', fontWeight: 600, margin: '32px 0 12px', borderBottom: `2px solid ${SECONDARY}`, paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 7 }}>
      {icon && <span style={{ color: SECONDARY, display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </h3>
  );
}

export default function EventAnalytics() {
  const { eventId } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredSponsorRow, setHoveredSponsorRow] = useState(null);
  const [hoveredAdvertRow, setHoveredAdvertRow] = useState(null);

  useEffect(() => {
    analyticsApi.getEventSummary(eventId)
      .then(res => setSummary(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444', fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>;
  if (!summary) return null;

  const imp = summary.impressions || {};
  const sponsors = summary.sponsor_breakdown || [];
  const adverts = summary.advert_board_breakdown || [];
  const daily = summary.daily_views || [];

  const sponsorClicks = sponsors.reduce((s, sp) => s + (sp.logo_clicks || 0), 0);
  const sponsorRedirects = sponsors.reduce((s, sp) => s + (sp.redirect_clicks || 0), 0);

  return (
    <div style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <h2 style={{ fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT, fontSize: '1.5rem', fontWeight: 700, marginTop: 0, marginBottom: 4 }}>
        Analytics — {summary.event?.name}
      </h2>
      <p style={{ color: '#8a9bb5', fontSize: '0.85rem', marginBottom: 24 }}>Live sponsor and scoreboard engagement data.</p>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <KpiCard label="Unique Scoreboard Viewers" value={imp.unique_viewers?.toLocaleString()} sub="distinct IPs" />
        <KpiCard label="Total Page Views" value={imp.total_views?.toLocaleString()} sub="includes repeat visits" />
        <KpiCard label="Sponsor Logo Clicks" value={sponsorClicks.toLocaleString()} sub="all sponsors combined" />
        <KpiCard label="Sponsor Website Redirects" value={sponsorRedirects.toLocaleString()} sub="followed through to sponsor site" />
      </div>

      {/* Daily views chart */}
      <SectionHeading icon={<IconMonitor size="1em" />}>Daily Scoreboard Views (Unique Viewers)</SectionHeading>
      <div style={{ background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 10, padding: '20px 16px' }}>
        <BarChart data={daily} />
      </div>

      {/* Sponsor performance table */}
      <SectionHeading icon={<IconStar size="1em" />}>Sponsor Performance</SectionHeading>
      {sponsors.length === 0 ? (
        <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>No sponsors configured for this event.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: TABLE_HEADER_BG, color: TABLE_HEADER_TEXT }}>
                {['Sponsor', 'Logo Clicks', 'Unique Clickers', 'Website Redirects', 'CTR', 'Conversion', 'Report'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap', color: '#000000' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sponsors.map((sp, i) => {
                const ctr = imp.unique_viewers > 0
                  ? ((sp.unique_logo_clickers / imp.unique_viewers) * 100).toFixed(1) + '%'
                  : '—';
                const conv = sp.unique_logo_clickers > 0
                  ? ((sp.unique_redirectors / sp.unique_logo_clickers) * 100).toFixed(1) + '%'
                  : '—';
                const isHovered = hoveredSponsorRow === sp.sponsor_id;
                return (
                  <tr
                    key={sp.sponsor_id}
                    onMouseEnter={() => setHoveredSponsorRow(sp.sponsor_id)}
                    onMouseLeave={() => setHoveredSponsorRow(null)}
                    style={{ background: isHovered ? SURFACE_HOVER : i % 2 === 0 ? SURFACE : SURFACE_ALT, borderBottom: `1px solid ${BORDER_COLOR}`, transition: 'background 0.15s', color: TEXT_LIGHT }}
                  >
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: TEXT_LIGHT }}>{sp.sponsor_name}</td>
                    <td style={{ padding: '10px 14px' }}>{sp.logo_clicks}</td>
                    <td style={{ padding: '10px 14px' }}>{sp.unique_logo_clickers}</td>
                    <td style={{ padding: '10px 14px' }}>{sp.redirect_clicks}</td>
                    <td style={{ padding: '10px 14px' }}>{ctr}</td>
                    <td style={{ padding: '10px 14px' }}>{conv}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <Link
                        to={`/events/${eventId}/sponsors/${sp.sponsor_id}/report`}
                        style={{ color: SECONDARY, fontWeight: 600, fontSize: '0.8rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        Report <IconArrowRight size="0.85em" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Advert board table */}
      <SectionHeading icon={<IconBarChart size="1em" />}>Advert Board Performance</SectionHeading>
      {adverts.length === 0 ? (
        <div style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>No advert boards assigned to this event.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: TABLE_HEADER_BG, color: TABLE_HEADER_TEXT }}>
                {['Advert Board', 'Total Clicks', 'Unique Clickers'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#000000' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adverts.map((ab, i) => {
                const isHovered = hoveredAdvertRow === ab.advert_board_id;
                return (
                  <tr
                    key={ab.advert_board_id}
                    onMouseEnter={() => setHoveredAdvertRow(ab.advert_board_id)}
                    onMouseLeave={() => setHoveredAdvertRow(null)}
                    style={{ background: isHovered ? SURFACE_HOVER : i % 2 === 0 ? SURFACE : SURFACE_ALT, borderBottom: `1px solid ${BORDER_COLOR}`, transition: 'background 0.15s', color: TEXT_LIGHT }}
                  >
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: TEXT_LIGHT }}>{ab.title}</td>
                    <td style={{ padding: '10px 14px' }}>{ab.total_clicks}</td>
                    <td style={{ padding: '10px 14px' }}>{ab.unique_clickers}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
